import React, { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Chart, registerables } from 'chart.js';

import { ConfigProvider } from 'antd';

// Shared UI Library Components
import {
  CAntdThemesConfig,
  PageLoader,
  useNotifications,
  useWindowHeight
} from '@ppmcore/seven-ppm-core-shared-components-react';

// import { loadStripe } from '@stripe/stripe-js';
// import { Elements } from '@stripe/react-stripe-js';

// Styles
import './app.scss';

// Interceptor
import { useInterceptor } from '../hooks/useInterceptor';

// ServiceWorker
import { useServiceWorker } from '../hooks/useServiceWorker';

// Global WebSockets connection hook
import { useWebSockets } from '../hooks/useWebSockets';

// Global Route Change hook
import { useRouterHistory } from '../hooks/useRouterHistory';

// Modules
import { Counsellors } from './counsellors/counsellors';
import { Auth } from './auth/auth';
import { SignIn } from './auth/sign-in/sign-in';
import { SignUp } from './auth/sign-up/sign-up';
import { SignUpConfirm } from './auth/sign-up-confirm/sign-up-confirm';
import { AccountVerification } from './auth/account-verification/account-verification';
import { MainLayout } from '../modules/main-layout/main-layout';
import { AuthGuard } from '../guards/auth.guard';
import { ForgotPassword } from './auth/forgot-password/forgot-password';
import { ResetPasswordResend } from './auth/reset-password-resend/reset-password-resend';
import { ResetPassword } from './auth/reset-password/reset-password';
import { CounsellorLayout } from '../modules/counsellor-layout/counsellor-layout';
import { PersonalData } from '../shared/components/counsellor-profile';
import { Rates } from '../shared/components/counsellor-profile';
import { ProfileLayout } from '../modules/profile-layout/profile-layout';
import {
  ChangeEmail,
  ChangePassword,
  DeleteAccount,
  NotificationSettings,
  UserData
} from '../shared/components/profile-settings';
import { Transactions } from './transactions/transactions';
import { Notifications } from './notifications/notifications';
import { BusinessProfileLayout } from '../modules/business-profile-layout/business-profile-layout';
import { BusinessInformation } from '../shared/components/business-profile';
import {
  CounsellorAttributes
} from '../shared/components/business-profile/counsellor-attributes/counsellor-attributes';
import { RecoveryLinkExpired } from './auth/recovery-link-expired/recovery-link-expired';
import { PermissionSettings } from '../shared/components/business-profile/permission-settings/permission-settings';
import { SessionSettings } from '../shared/components/business-profile/session-settings/session-settings';
import { TermsOfService } from '../shared/components/business-profile/terms-of-service/terms-of-service';
import { PrivacyPolicy } from '../shared/components/business-profile/privacy-policy/privacy-policy';
import { Dashboard } from './dashboard/dashboard';
// import { Faq } from './faq/faq';
import { PageNotFound } from './page-not-found/page-not-found';
import { PaymentDetails } from '../shared/components/business-profile/payment-details/payment-details';
import { Billing } from '../shared/components/business-profile/billing/billing';

// Store entities
import { fetchCompanyFee, fetchCountries, fetchLanguages, fetchSpecialities } from '../store/core/core.thunks';
import { getUserProfile, userLogout } from '../store/user/user.thunks';
import { fetchSocketToken } from '../store/socket/socket.thunks';
import { fetchChartsInfo, fetchConsultationChartsInfo } from '../store/dashboard/dashboard.thunks';
import { getStaticPages } from '../store/static-pages/static-pages.thunks';
import { fetchCompanyProfile, updateCompanyBilling } from '../store/company/company.thunks';
import { fetchCompanyCurrencies, fetchCurrentCurrency } from '../store/currency/currency.thunks';
import { getNotificationsData } from '../store/app-notifications/app-notifications.selectors';
import { getLastMessageData } from '../store/socket/socket.selectors';

// Models
// import { CStripeOptions } from '../constantes/global.constants';
import { EWebSocketTypes } from '../enums/web-socket-types.enums';
import { ThemeAndSettings } from '../shared/components/business-profile/theme-and-settings/theme-and-settings';

// Register charts
Chart.register(...registerables);

// Make sure to call `loadStripe` outside a component’s render to avoid
// recreating the `Stripe` object on every render.
// const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISH_KEY as string);

export const App = () => {
  // set up http interceptor
  useInterceptor();
  // set up service worker
  useServiceWorker();
  // set up web sockets
  useWebSockets();
  // set up router history handler
  useRouterHistory();
  // set up window resize handler
  useWindowHeight();

  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();

  const notificationsData = useSelector(getNotificationsData);

  const redirectTo = (path: string): void => {
    navigate(path);
  }

  // set up notifications
  const { contextHolder } = useNotifications({ notificationsState: notificationsData, redirectTo });

  const [indexPath, setIndexPath] = useState('');

  const lastMessage = useSelector(getLastMessageData);
  const token = localStorage.getItem('token');

  useEffect(() => {
    dispatch(getStaticPages());

    setIndexPath(token ? '/home' : '/sign-in');
    if (token) {
      // Fetch static information
      dispatch(fetchChartsInfo({}));
      dispatch(fetchConsultationChartsInfo());
      dispatch(fetchLanguages());
      dispatch(fetchCompanyCurrencies());
      dispatch(fetchCurrentCurrency());
      dispatch(fetchCountries());

      if (location.pathname !== '/home') dispatch(fetchCompanyProfile());

      dispatch(fetchCompanyFee());
      dispatch(fetchSpecialities());
      dispatch(getUserProfile());
      dispatch(fetchSocketToken());
    }
  }, [token, dispatch]);

  useEffect(() => {
    if (!token) return;
    if (lastMessage?.type === EWebSocketTypes.CompanyBlocked) {
      dispatch(userLogout());
    }
    if (lastMessage?.type === EWebSocketTypes.CompanyBillingUpdated) {
      dispatch(updateCompanyBilling(lastMessage.data))
    }
  }, [lastMessage, token]);

  if (!indexPath) {
    // TODO add global loader;
    return <>Loading...</>;
  }

  return (
    <ConfigProvider theme={ CAntdThemesConfig }>
      {/* TODO add boolean store from store */ }
      <PageLoader show={ false }/>
      { contextHolder }
      {/*<Elements stripe={ stripePromise } options={ CStripeOptions }>*/ }

      <Routes>
        <Route path="/" element={ <Navigate to="/sign-in" replace/> }/>
        <Route path="" element={ <AuthGuard isProtectedRouter={ false } hasToken={ !!token } children={ <Auth/> }/> }>
          <Route path="sign-in" element={ <SignIn/> }/>
          <Route path="sign-up" element={ <SignUp/> }/>
          <Route path="sign-up-confirm" element={ <SignUpConfirm/> }/>
          <Route path="account-verification" element={ <AccountVerification/> }/>
          <Route path="forgot-password" element={ <ForgotPassword/> }/>
          <Route path="forgot-password-resend" element={ <ResetPasswordResend/> }/>
          <Route path="reset-password" element={ <ResetPassword/> }/>
          <Route path="recovery-link-expired" element={ <RecoveryLinkExpired/> }/>
        </Route>

        <Route path="" element={ <AuthGuard hasToken={ !!token } children={ <MainLayout/> }/> }>
          <Route path="home" element={ <Dashboard/> }/>
          <Route path="counsellors">
            <Route path="" element={ <Counsellors/> }/>
            <Route path=":id" element={ <CounsellorLayout/> }>
              <Route path="" element={ <PersonalData/> }/>
              <Route path="rates" element={ <Rates/> }/>

              {/* TODO next release */ }
              {/*<Route path="reviews" element={ <Reviews/> }/>*/ }
            </Route>
          </Route>
          <Route path="profile-settings" element={ <ProfileLayout/> }>
            <Route path="" element={ <UserData/> }/>
            <Route path="change-password" element={ <ChangePassword/> }/>
            <Route path="change-email" element={ <ChangeEmail/> }/>
            <Route path="notification-settings" element={ <NotificationSettings/> }/>
            <Route path="delete-account" element={ <DeleteAccount/> }/>
          </Route>
          <Route path="transactions" element={ <Transactions/> }/>
          {/*<Route path="refunds" element={ <>Refunds</> }/>*/ }
          <Route path="business-profile" element={ <BusinessProfileLayout/> }>
            <Route path="" element={ <BusinessInformation/> }/>

            {/* TODO next release */ }
            {/*<Route path="logo-settings" element={ <>Logo Settings</> } />*/ }
            {/*<Route path="theme-settings" element={ <>Theme Settings</> } />*/ }
            {/*<Route path="illustrations-settings" element={ <>Illustrations Settings</> } />*/ }
            {/*<Route path="widget-settings" element={ <>Widget Settings</> } />*/ }

            <Route path="theme-and-widget" element={ <ThemeAndSettings/> }/>
            <Route path="payment-details" element={ <PaymentDetails/> }/>
            <Route path="billing" element={ <Billing/> }/>
            <Route path="counsellor-attributes" element={ <CounsellorAttributes/> }/>
            <Route path="permission-settings" element={ <PermissionSettings/> }/>
            <Route path="consultations-settings" element={ <SessionSettings/> }/>
            <Route path="terms-of-service" element={ <TermsOfService/> }/>
            <Route path="privacy-policy" element={ <PrivacyPolicy/> }/>
          </Route>
          <Route path="notifications" element={ <Notifications/> }/>
          {/*<Route path="subscription" element={ <>Subscription</> }/>*/ }
          {/*<Route path="rating" element={ <>Rating</> }/>*/ }
          {/*<Route path="clients" element={ <>Clients</> }/>*/ }
          {/*<Route path="faq" element={ <Faq/> }/>*/ }
          {/*<Route path="terms-of-service" element={ <>terms-of-service</> }/>*/ }
          {/*<Route path="privacy-policy" element={ <>privacy-policy</> }/>*/ }
        </Route>

        {/*<Route path="connect-stripe-account"*/ }
        {/*       element={ <AuthGuard hasToken={ !!token } children={ <ConnectStripeAccount/> }/> }/>*/ }
        <Route path="404" element={ <PageNotFound/> }/>
        <Route path="*" element={ <PageNotFound/> }/>
      </Routes>
      {/*</Elements>*/ }
    </ConfigProvider>
  );
}
